/**
 * One supplier's figures at each customer site it covers — the per-site
 * breakdown under a Supplier Performance row. Customer review 11 Sep (review
 * msg 3/4).
 *
 * Each site is asked of the SAME list endpoint the page uses, with the page's
 * filters and date window and the view scope replaced by that one site (see
 * `buildSiteRowParams` in useSupplierPerformance). So every breakdown row is
 * computed by the definitions behind the supplier row above it, and the
 * backend intersects `site` with the viewer's grant — a site the user does not
 * hold comes back empty rather than wider.
 */
import { suppliersAPI, SupplierProfile, SupplierCustomerSite } from '../../lib/api';
import { useFetchData } from '../../hooks/useFetchData';

export interface SiteBreakdownRow {
  site: SupplierCustomerSite;
  /** Null when the endpoint returned no row for that site (nothing in scope). */
  profile: SupplierProfile | null;
}

/**
 * Fetch a supplier's per-site rows while `enabled`.
 *
 * @param supplierId - the supplier company id
 * @param sites - the customer sites its top-level row covers
 * @param buildParams - the page's params for one site × this supplier
 * @param enabled - fetch only once the row is expanded
 * @returns `{ rows, loading, error, refetch }`
 */
export function useSiteBreakdown(
  supplierId: number,
  sites: SupplierCustomerSite[],
  buildParams: (siteId: number, supplierId: number) => Record<string, string | number | boolean>,
  enabled: boolean,
) {
  // Refetch when the page's request changes (filters, dates, currency), not
  // when the builder's identity does.
  const paramsKey = JSON.stringify(buildParams(0, supplierId));
  const sitesKey = sites.map((s) => s.id).join(',');
  return useFetchData<SiteBreakdownRow[]>(
    async () => {
      if (!enabled) return [];
      const responses = await Promise.all(
        sites.map((site) => suppliersAPI.getProfiles(buildParams(site.id, supplierId))),
      );
      return sites.map((site, i) => ({
        site,
        profile: responses[i].data.results.find((r) => r.company === supplierId) ?? null,
      }));
    },
    { initialLoading: enabled, deps: [enabled, supplierId, sitesKey, paramsKey] },
  );
}
